<script>
import { Loader } from "@googlemaps/js-api-loader";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";

import SimpleBar from "simplebar";
import "simplebar/dist/simplebar.css";
import ResizeObserver from "resize-observer-polyfill";
window.ResizeObserver = ResizeObserver;

import { styles } from "./styles.js";
let google = {
  maps: null,
  marker: null,
};

export default {
  props: {
    regions: Array,
  },
  data() {
    return {
      locationNumbers: {},
      map: null,
      activeLocation: null,
      markers: {},
      cluster: null,
      lastZIndex: 0,
      simpleBar: null,
    };
  },
  async mounted() {
    let currentLocationNumber = 1;
    this.regions.forEach((region) => {
      region.locations.forEach((location) => {
        this.$set(this.locationNumbers, location.name, currentLocationNumber);
        currentLocationNumber++;
      });
    });

    this.simpleBar = new SimpleBar(this.$refs.regions, {
      autoHide: false,
    });

    await this.loadLibraries();
    this.loadMap();
  },
  methods: {
    async loadLibraries() {
      const loader = new Loader({
        apiKey: "AIzaSyDm6O_3SIywukLJ83scNFe-n1rpOZmmFkA",
        version: "weekly",
      });

      const Maps = await loader.importLibrary("maps");
      google.maps = Maps;
      const Marker = await loader.importLibrary("marker");
      google.marker = Marker;
    },
    loadMap() {
      this.map = new google.maps.Map(this.$refs.map, {
        mapId: "e086f7cfec2b446a",
        center: { lat: 53.9449757, lng: -2.5414785 },
        zoom: 6,
        minZoom: 6,
        zoomControl: true,
        disableDefaultUI: true,
      });

      this.lastZIndex = google.marker.Marker.MAX_ZINDEX;

      this.map.mapTypes.set(
        "styled_map",
        new google.maps.StyledMapType(styles)
      );
      this.map.setMapTypeId("styled_map");

      this.createCluster();

      this.addMarkers();
    },
    addMarkers() {
      this.regions.forEach((region) => {
        region.locations.forEach((location) => {
          const marker = this.createMarker(region, location);
          marker.addListener("click", () => {
            this.activateLocation(location);
          });
          this.$set(this.markers, location.name, marker);
          this.cluster.addMarker(marker);
        });
      });
    },
    activateLocation(location) {
      this.activeLocation = location;
      for (const markerKey in this.markers) {
        if (Object.hasOwnProperty.call(this.markers, markerKey)) {
          const marker = this.markers[markerKey];
          if (location.name == markerKey) {
            marker.content.classList.add("-active");
            this.zoomTo(marker.position);
            marker.zIndex = this.lastZIndex;
            this.lastZIndex++;
          } else {
            marker.content.classList.remove("-active");
          }
        }
      }
      this.simpleBar.getScrollElement().scrollTop =
        this.$refs[`location-${location.name}`][0].offsetTop -
        this.simpleBar.getScrollElement().offsetTop;
    },
    zoomTo(latLng) {
      if (this.map.getZoom() < 12) {
        this.map.setZoom(12);
      }
      const mapBounds = this.map.getBounds();
      if (!mapBounds.contains(latLng)) {
        this.map.panTo(latLng);
      }
    },
    createCluster() {
      const markerAlgorithm = new SuperClusterAlgorithm({
        maxZoom: 12,
        radius: 43,
      });
      this.cluster = new MarkerClusterer({
        map: this.map,
        markerAlgorithm,
        renderer: {
          render(cluster) {
            const template = document.createElement("template");
            template.innerHTML = `<div class="map-cluster">
              <p class="map-cluster__title">${cluster.markers.length}</p>
            </div>`;

            return new google.marker.AdvancedMarkerElement({
              position: cluster.position,
              content: template.content.children[0],
            });
          },
        },
      });
    },
    getPinImage(colour) {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 42" style="enable-background:new 0 0 30 42" xml:space="preserve">
        <path d="M15 0c8.3 0 15 6.7 15 14.9S21 34 15.8 41.6c-.3.6-1.1.6-1.6 0C9.1 34 0 23.2 0 14.9S6.7 0 15 0z" style="fill:${colour}"/>
        <path d="M14.9 7.6c4.1 0 7.4 3.3 7.4 7.2 0 4-3.3 7.3-7.4 7.3-4 0-7.3-3.3-7.3-7.3.1-4 3.4-7.2 7.3-7.2z" style="fill:#fff"/>
      </svg>`;
    },
    createPin(colour) {
      const template = document.createElement("template");
      template.innerHTML = `<div class="map-pin">
            ${this.getPinImage(colour)}
            <p class="map-pin__title"></p>
      </div>`;
      return template.content.children[0];
    },
    createMarker(region, location) {
      const pin = this.createPin(
        region.colour,
        this.locationNumbers[location.name]
      );

      const marker = new google.marker.AdvancedMarkerElement({
        position: {
          lat: parseFloat(location.lat),
          lng: parseFloat(location.lng),
        },
        content: pin,
      });
      return marker;
    },
  },
};
</script>

<template>
  <div class="regions-map">
    <div class="regions" ref="regions">
      <div class="region" v-for="region in regions" :key="region.name">
        <label class="region-name text">{{ region.name }}</label>
        <div class="locations">
          <button
            class="location"
            :class="{ '-active': activeLocation == location }"
            v-for="location in region.locations"
            :key="location.name"
            :ref="`location-${location.name}`"
            @click="activateLocation(location)"
          >
            <div class="location-pin" v-html="getPinImage(region.colour)"></div>
            <p class="name text -t2">
              {{ locationNumbers[location.name] }}. {{ location.name }}
            </p>
          </button>
        </div>
      </div>
    </div>

    <div class="map" ref="map"></div>
  </div>
</template>

<style lang="scss" scoped>
@import "foundation-sites/scss/foundation.scss";
@import "@/assets/scss/_settings";

.regions-map {
  display: flex;
  width: 100%;
  justify-content: space-between;
  flex-direction: column;
  gap: 20px;
  padding: 40px 0;
  @include breakpoint(medium) {
  }
  @include breakpoint(large) {
    gap: 0;
    flex-direction: row;
    height: 500px;
  }
  @include breakpoint(xlarge) {
    height: 700px;
  }
}

.regions {
  color: #27348b;
  // overflow-x: auto;
  width: 100%;
  order: 2;
  max-height: 400px;
  @include breakpoint(large) {
    order: 1;
    max-height: initial;
    max-width: 320px;
    padding-right: 20px;
  }
  @include breakpoint(xlarge) {
    max-width: 420px;
  }
}
.region {
  border-bottom: 1px solid #813a85;
  padding: 20px 0;
}
.region-name {
  margin-bottom: 20px;
  display: block;
}
.location {
  display: flex;
  gap: 20px;
  width: 100%;
  align-items: center;
  margin-bottom: 20px;
  transition: background-color 0.2s;
  padding: 5px;
  text-align: left;
  &.-active {
    background-color: rgba(#a074a8, 0.2);
  }
}
.location-pin {
  width: 30px;
  height: 42px;
}

.map {
  height: 300px;
  width: 100%;
  order: 1;
  @include breakpoint(large) {
    order: 2;
    width: auto;
    height: 100%;
    flex: 1 0 auto;
  }
  &::v-deep .map-cluster {
    position: relative;
    width: 40px;
    height: 40px;
    border: 7px solid rgba(#6c2477, 0.6);
    border-radius: 50%;
    box-shadow: rgba(#813a85, 0.6) 0 0 10px 1px;
    &::before {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 50%;
      left: 50%;
      border-radius: 50%;
      content: "";
      border: 3px solid #6c2477;
      transform: translate(-50%, -50%);
      background-color: #fff;
      box-shadow: inset rgba(#813a85, 0.4) 0 0 3px 1px;
    }
    &__title {
      position: absolute;
      top: 50%;
      left: 50%;
      color: #6c2477;
      transform: translate(-50%, -50%);
      font: bold 12px/1 CenturyGothic;
    }
  }
  &::v-deep .map-pin {
    position: relative;
    width: 40px;
    height: 63px;
    transition: transform 0.2s;
    &.-active {
      transform: scale(1.2);
    }
    &__title {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, 13px);
      width: 14px;
      height: 13px;
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      background-image: url("~@/assets/images/house.png");
    }
  }
}
</style>

<style lang="scss">
.simplebar-content-wrapper {
  scroll-behavior: smooth;
}
.simplebar-track {
  background-color: #eeeeee;
  border-radius: 7px;
}
.simplebar-scrollbar::before {
  border-radius: 5px;
  background-color: rgba(#a074a8, 0.5);
}
</style>
